// frontend/src/components/quiz/QuestionCard.js
import React, { useState, useEffect } from "react";
import { CheckCircle, XCircle, AlertCircle, ExternalLink, Maximize2 } from "lucide-react";
import ImageZoomModal from "../common/ImageZoomModal";
import { useLanguage } from "../../contexts/LanguageContext";
import { getQuizTranslation } from "../../utils/quizTranslations";

const QuestionCard = ({
  question,
  selectedAnswers = [],
  onAnswerSelect,
  onSubmitAnswer,
  showFeedback = false,
  feedback = null,
  disabled = false,
}) => {
  const { language, t } = useLanguage();
  const [imageError, setImageError] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(() => {
    // Try language-specific image first if not German
    if (question.image_url && language !== 'de') {
      return question.image_url.replace('.jpg', `-${language}.jpg`);
    }
    return question.image_url;
  });

  // Reset image state when question changes
  useEffect(() => {
    setImageError(false);
    if (question.image_url && language !== 'de') {
      setImageSrc(question.image_url.replace('.jpg', `-${language}.jpg`));
    } else {
      setImageSrc(question.image_url);
    }
  }, [question.id, question.image_url, language]);

  const isMultipleSelect = question.question_type === "multiple_select";
  const isTrueFalse = question.question_type === "true_false";

  const handleAnswerClick = (answerId) => {
    if (disabled) return;

    if (isMultipleSelect) {
      // Toggle answer in array - don't submit yet (shouldSubmit = false)
      if (selectedAnswers.includes(answerId)) {
        onAnswerSelect(selectedAnswers.filter((id) => id !== answerId), false);
      } else {
        onAnswerSelect([...selectedAnswers, answerId], false);
      }
    } else {
      // Single selection - submit immediately (shouldSubmit = true, default)
      onAnswerSelect([answerId]);
    }
  };

  const isAnswerSelected = (answerId) => selectedAnswers.includes(answerId);

  const getAnswerClassName = (answerId) => {
    const isSelected = isAnswerSelected(answerId);
    const baseClasses =
      "w-full p-4 rounded-lg border-2 transition-all duration-200 text-left flex items-center";

    if (showFeedback && feedback) {
      // Show correct/incorrect styling using 4-state logic
      const isCorrect = feedback.correct_answer_ids?.includes(answerId);

      if (isSelected && isCorrect) {
        // Selected + Correct: Strong green with background
        return `${baseClasses} border-[#84BD00] bg-[#84BD00] bg-opacity-20 ring-2 ring-[#84BD00]`;
      } else if (isSelected && !isCorrect) {
        // Selected + Incorrect: Red with background
        return `${baseClasses} border-[#DA291C] bg-[#DA291C] bg-opacity-20 ring-2 ring-[#DA291C]`;
      } else if (!isSelected && isCorrect) {
        // Unselected + Correct: Green background with dashed border (user missed this)
        return `${baseClasses} border-[#84BD00] border-2 border-dashed bg-[#84BD00] bg-opacity-10`;
      } else {
        // Unselected + Incorrect: Gray faded (correctly left blank)
        return `${baseClasses} border-gray-200 bg-gray-50 opacity-60`;
      }
    }

    if (isSelected) {
      // Primary blue when selected: #00A3E0
      return `${baseClasses} border-[#00A3E0] bg-[#00A3E0] bg-opacity-10`;
    }

    if (disabled) {
      return `${baseClasses} border-gray-200 opacity-60 cursor-not-allowed`;
    }

    return `${baseClasses} border-gray-300 hover:border-[#00A3E0] hover:bg-[#00A3E0] hover:bg-opacity-5 cursor-pointer`;
  };

  const getAnswerIcon = (answerId) => {
    if (!showFeedback || !feedback) {
      // Show checkbox for multiple_select, radio button for single choice
      const isSelected = isAnswerSelected(answerId);

      if (isMultipleSelect) {
        // Checkbox for multiple select
        return (
          <div
            className={`w-5 h-5 rounded border-2 mr-3 flex-shrink-0 ${
              isSelected
                ? "border-[#04A777] bg-[#04A777]"
                : "border-gray-400"
            }`}
          >
            {isSelected && (
              <svg className="w-full h-full text-white" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        );
      } else {
        // Radio button for single choice
        return (
          <div
            className={`w-5 h-5 rounded-full border-2 mr-3 flex-shrink-0 ${
              isSelected
                ? "border-[#04A777] bg-white"
                : "border-gray-400"
            }`}
          >
            {isSelected && (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-2.5 h-2.5 bg-[#04A777] rounded-full"></div>
              </div>
            )}
          </div>
        );
      }
    }

    // Show feedback icons with 4-state logic
    const isCorrect = feedback.correct_answer_ids?.includes(answerId);
    const isSelected = isAnswerSelected(answerId);

    if (isSelected && isCorrect) {
      // Selected + Correct: Full green checkmark
      return <CheckCircle className="w-5 h-5 text-[#84BD00] mr-3 flex-shrink-0" />;
    } else if (isSelected && !isCorrect) {
      // Selected + Incorrect: Full red X
      return <XCircle className="w-5 h-5 text-[#DA291C] mr-3 flex-shrink-0" />;
    } else if (!isSelected && isCorrect) {
      // Unselected + Correct: Faded green checkmark (missed answer)
      return <CheckCircle className="w-5 h-5 text-[#84BD00] mr-3 flex-shrink-0 opacity-40" />;
    }

    // Unselected + Incorrect: No icon
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Question Text */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-[#002855]">
          {t(question.question_text)}
        </h2>

        {/* Optional hint/description */}
        {question.hint && (
          <p className="text-sm text-gray-600 italic">
            {t(question.hint)}
          </p>
        )}

        {/* Question Image (if exists) */}
        {question.image_url && !imageError && (
          <>
            <div
              className="relative rounded-lg overflow-hidden bg-gray-100 cursor-zoom-in"
              onClick={() => setIsImageModalOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setIsImageModalOpen(true)}
              aria-label={getQuizTranslation("ui.clickToEnlarge", language)}
            >
              <img
                src={imageSrc}
                alt={question.image_alt_text ? t(question.image_alt_text) : "Question image"}
                className="w-full max-h-96 object-contain"
                onError={() => {
                  // Try fallback to common image if language-specific failed
                  if (imageSrc !== question.image_url) {
                    setImageSrc(question.image_url);
                  } else {
                    // No fallback available, hide image
                    setImageError(true);
                  }
                }}
              />
              {/* Fullscreen indicator icon */}
              <div className="absolute bottom-2 right-2 bg-black/50 rounded p-1.5">
                <Maximize2 className="w-4 h-4 text-white" />
              </div>
            </div>
            <ImageZoomModal
              isOpen={isImageModalOpen}
              onClose={() => setIsImageModalOpen(false)}
              imageSrc={imageSrc}
              imageAlt={question.image_alt_text ? t(question.image_alt_text) : "Question image"}
            />
          </>
        )}

        {/* Instruction text */}
        <p className="text-sm text-gray-600">
          {isMultipleSelect
            ? getQuizTranslation("ui.selectAnswers", language)
            : getQuizTranslation("ui.selectAnswer", language)}
        </p>
      </div>

      {/* Answers */}
      <div className="space-y-3">
        {question.answers.map((answer) => (
          <button
            key={answer.id}
            type="button"
            onClick={() => handleAnswerClick(answer.id)}
            disabled={disabled}
            className={getAnswerClassName(answer.id)}
          >
            {getAnswerIcon(answer.id)}
            <span className="flex-1">{t(answer.answer_text)}</span>
          </button>
        ))}
      </div>

      {/* Submit Button for Multiple Select */}
      {isMultipleSelect && !feedback && (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={onSubmitAnswer}
            disabled={selectedAnswers.length === 0}
            className="px-6 py-2.5 rounded-lg bg-[#04A777] text-white font-medium hover:bg-[#038c5f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {language === 'en' ? 'Submit Answer' : 'Antwort abschicken'}
          </button>
        </div>
      )}

      {/* Feedback Section - Corporate Colors */}
      {showFeedback && feedback && (
        <div
          className={`p-4 rounded-lg border-2 ${
            feedback.is_correct
              ? "bg-[#84BD00] bg-opacity-10 border-[#84BD00]"
              : "bg-[#DA291C] bg-opacity-10 border-[#DA291C]"
          }`}
        >
          <div className="flex items-start gap-3">
            {feedback.is_correct ? (
              <CheckCircle className="w-6 h-6 text-[#84BD00] flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-6 h-6 text-[#DA291C] flex-shrink-0 mt-0.5" />
            )}

            <div className="flex-1">
              <h3
                className={`font-semibold mb-2 ${
                  feedback.is_correct ? "text-[#002855]" : "text-[#002855]"
                }`}
              >
                {feedback.is_correct
                  ? getQuizTranslation("ui.correct", language)
                  : getQuizTranslation("ui.incorrect", language)}
              </h3>

              {feedback.explanation && (
                <p className="text-sm text-gray-600">
                  {t(feedback.explanation)}
                </p>
              )}

              {feedback.learn_more_url && (
                <a
                  href={feedback.learn_more_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-sm text-[#00A3E0] hover:text-[#EE005F] font-medium transition-colors"
                >
                  {getQuizTranslation("ui.learnMore", language)}
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
